import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { api, profilePdfUrl } from "@/lib/api";

function PartnersStrip({ section }) {
  const [partners, setPartners] = useState(null);
  useEffect(() => {
    api.get("/partners").then((r) => setPartners(r.data || [])).catch(() => setPartners([]));
  }, []);
  const live = partners && partners.length > 0;
  const items = live ? partners : (section.items || []);
  return (
    <section className="bg-[hsl(var(--aoa-gray-bg))]" data-testid={`section-${section.key}`}>
      <div className="aoa-container py-16">
        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55 }} className="text-center max-w-3xl mx-auto mb-8">
          <span className="aoa-eyebrow justify-center">Community</span>
          <h2 className="aoa-h2 mt-3 text-[hsl(var(--aoa-navy))]">{section.heading}</h2>
        </motion.div>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          {items.map((it, i) => {
            const name = live ? it.name : (it.label || it.name || it);
            const logo = live ? it.logo_url : null;
            const href = live ? it.website_url : null;
            const inner = (
              <>
                {logo ? (
                  <img src={logo} alt={name} width={120} height={60} loading="lazy" decoding="async" className="mx-auto h-10 w-auto object-contain mb-2" />
                ) : null}
                <div className="text-center text-sm font-semibold text-[hsl(var(--aoa-navy))]">{name}</div>
              </>
            );
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: i * 0.04 }} className="aoa-card" data-testid={`partner-card-${i}`}>
                {href ? <a href={href} target="_blank" rel="noreferrer">{inner}</a> : inner}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

function Icon({ name, className }) {
  const C = (name && Icons[name]) || Icons.Circle;
  return <C className={className} aria-hidden />;
}

function CTA({ cta, isHero = false }) {
  const isExternal = cta.href?.startsWith("/api/") || cta.href?.startsWith("http");
  const cls =
    cta.variant === "ghost"
      ? isHero
        ? "aoa-btn-ghost"
        : "aoa-btn-outline"
      : "aoa-btn-primary";
  if (isExternal) {
    return (
      <a
        href={cta.href}
        className={cls}
        data-testid={`cta-${cta.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        download={cta.download || undefined}
      >
        {cta.label}
        <Icons.ArrowUpRight className="h-4 w-4" />
      </a>
    );
  }
  return (
    <Link
      to={cta.href}
      className={cls}
      data-testid={`cta-${cta.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      {cta.label}
      <Icons.ArrowRight className="h-4 w-4" />
    </Link>
  );
}

export default function Section({ section, pageTitle, isFirst = false }) {
  // When this is the FIRST section on a page and the type doesn't naturally
  // produce an <h1>, render the heading inside an <h1>.
  const HeadingTag = isFirst ? "h1" : "h2";
  switch (section.type) {
    case "hero":
      return (
        <section className="relative isolate overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0">
            <picture>
              <source srcSet={section.image && section.image.replace(/\.(png|jpe?g)$/i, ".webp")} type="image/webp" />
              <img
                src={section.image}
                alt={section.image_alt || ""}
                width={1920}
                height={1080}
                loading="eager"
                decoding="sync"
                fetchPriority="high"
                className="h-full w-full object-cover"
              />
            </picture>
            <div className="absolute inset-0 aoa-gradient-overlay" />
            <div className="absolute inset-0 aoa-grain opacity-40" />
            {/* Animated airplane-trail SVG */}
            <svg
              viewBox="0 0 1440 600"
              className="absolute inset-0 w-full h-full pointer-events-none"
              preserveAspectRatio="none"
              aria-hidden="true"
              data-testid="hero-flight-path"
            >
              <motion.path
                d="M 80 480 Q 720 80 1360 280"
                stroke="rgba(125,211,252,0.55)"
                strokeWidth="2"
                strokeDasharray="6 6"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2.2, ease: "easeInOut" }}
              />
              <motion.g
                initial={{ offsetDistance: "0%", opacity: 0 }}
                animate={{ offsetDistance: "100%", opacity: 1 }}
                transition={{ duration: 2.2, ease: "easeInOut" }}
                style={{ offsetPath: 'path("M 80 480 Q 720 80 1360 280")', offsetRotate: "auto" }}
              >
                <path d="M 0 -7 L 18 0 L 0 7 L 6 0 Z" fill="rgba(255,255,255,0.92)" />
              </motion.g>
            </svg>
          </div>
          <div className="relative aoa-container py-32 md:py-44 lg:py-52">
            <motion.div {...fadeUp} className="max-w-3xl">
              <span className="aoa-eyebrow text-[hsl(var(--aoa-sky-light))]">
                <Icons.Plane className="h-3.5 w-3.5" />
                Aviation Coordination · Somalia
              </span>
              <h1 className="aoa-h1 mt-5 text-white">
                {section.heading}
              </h1>
              <p className="mt-6 max-w-2xl text-lg md:text-xl text-slate-200/95 leading-relaxed">
                {section.body}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                {(section.ctas || []).map((c) => (
                  <CTA key={c.label} cta={c} isHero />
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      );

    case "statement":
      return (
        <section className="bg-[hsl(var(--aoa-gray-bg))]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20 md:py-24">
            <motion.div {...fadeUp} className="mx-auto max-w-4xl text-center">
              {section.heading && (
                isFirst ? (
                  <h1 className="aoa-h2 text-[hsl(var(--aoa-navy))] mb-6">{section.heading}</h1>
                ) : (
                  <span className="aoa-eyebrow justify-center">{section.heading}</span>
                )
              )}
              <p className="mt-5 font-display text-2xl md:text-3xl lg:text-4xl leading-snug text-[hsl(var(--aoa-navy))]">
                “{section.body}”
              </p>
            </motion.div>
          </div>
        </section>
      );

    case "stat_grid":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.div {...fadeUp} className="mb-12 max-w-2xl">
              <span className="aoa-eyebrow">Snapshot</span>
              <h2 className="aoa-h2 mt-3 text-[hsl(var(--aoa-navy))]">
                {section.heading}
              </h2>
            </motion.div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {section.items.map((it, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="aoa-card flex items-start gap-4"
                >
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-[hsl(var(--aoa-gray-bg))]">
                    <Icon name={it.icon} className="h-5 w-5 text-[hsl(var(--aoa-royal))]" />
                  </div>
                  <div className="font-display text-base font-semibold text-[hsl(var(--aoa-navy))] leading-snug">
                    {it.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "feature_grid":
      return (
        <section className="bg-[hsl(var(--aoa-gray-bg))]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20 md:py-24">
            <motion.div {...fadeUp} className="mb-12 max-w-2xl">
              <span className="aoa-eyebrow">Our Work</span>
              <h2 className="aoa-h2 mt-3 text-[hsl(var(--aoa-navy))]">
                {section.heading}
              </h2>
            </motion.div>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {section.items.map((it, i) => (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="aoa-card group"
                >
                  <div className="mb-5 grid h-12 w-12 place-items-center rounded-xl bg-[hsl(var(--aoa-royal))]/8 transition-colors group-hover:bg-[hsl(var(--aoa-royal))]/15">
                    <Icon name={it.icon} className="h-5 w-5 text-[hsl(var(--aoa-royal))]" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">
                    {it.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{it.body}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      );

    case "value_chips":
      return (
        <section className="bg-white border-y border-slate-200" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-16">
            <motion.div {...fadeUp} className="mb-8 max-w-2xl">
              <span className="aoa-eyebrow">Principles</span>
              <h2 className="aoa-h2 mt-3 text-[hsl(var(--aoa-navy))]">{section.heading}</h2>
            </motion.div>
            <div className="flex flex-wrap gap-3">
              {section.items.map((v, i) => (
                <motion.div
                  key={v.name}
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.03 }}
                  title={v.body}
                  className="aoa-chip cursor-default"
                >
                  <Icons.CheckCircle2 className="h-4 w-4 text-[hsl(var(--aoa-royal))]" />
                  {v.name}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "value_grid":
      return (
        <section className="bg-[hsl(var(--aoa-gray-bg))]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.div {...fadeUp} className="mb-12 max-w-2xl">
              <span className="aoa-eyebrow">Principles</span>
              <HeadingTag className={`mt-3 text-[hsl(var(--aoa-navy))] ${isFirst ? "aoa-h1" : "aoa-h2"}`}>{section.heading}</HeadingTag>
            </motion.div>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {section.items.map((v, i) => (
                <motion.div
                  key={v.name}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.04 }}
                  className="aoa-card"
                >
                  <div className="font-display text-lg font-semibold text-[hsl(var(--aoa-royal))]">
                    {v.name}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "split_imagery":
      return (
        <section className="bg-[hsl(var(--aoa-navy))] text-white" data-testid={`section-${section.key}`}>
          <div className="relative aoa-grain opacity-40 absolute inset-0 pointer-events-none" />
          <div className="aoa-container py-20">
            <motion.h2 {...fadeUp} className="aoa-h2 max-w-3xl">
              {section.heading}
            </motion.h2>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {section.items.map((it, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="relative overflow-hidden rounded-2xl border border-white/10 aspect-[4/3]"
                >
                  <img src={it.image} alt={it.alt || ""} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--aoa-navy))]/80 to-transparent" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "callout":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-2xl border-l-4 border-[hsl(var(--aoa-royal))] bg-[hsl(var(--aoa-gray-bg))] p-8 md:p-12"
            >
              <Icons.Quote className="absolute right-6 top-6 h-10 w-10 text-[hsl(var(--aoa-royal))]/15" />
              {section.heading && (
                isFirst ? (
                  <h1 className="aoa-h2 text-[hsl(var(--aoa-navy))]">{section.heading}</h1>
                ) : (
                  <h3 className="aoa-h3 text-[hsl(var(--aoa-navy))]">{section.heading}</h3>
                )
              )}
              <p className="mt-3 text-lg md:text-xl leading-relaxed text-slate-700 max-w-4xl">
                {section.body}
              </p>
            </motion.div>
          </div>
        </section>
      );

    case "cta_strip":
      return (
        <section className="bg-[hsl(var(--aoa-navy))]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-16">
            <motion.div
              {...fadeUp}
              className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            >
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-white max-w-2xl">
                {section.heading}
              </h2>
              <div className="flex flex-wrap gap-3">
                {(section.ctas || []).map((c) => (
                  <CTA key={c.label} cta={c} isHero />
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      );

    case "rich_text":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] items-center">
              <motion.div {...fadeUp}>
                <span className="aoa-eyebrow">{pageTitle}</span>
                <h1 className="aoa-h1 mt-4 text-[hsl(var(--aoa-navy))]">{section.heading}</h1>
                <p className="aoa-body mt-6 text-lg">{section.body}</p>
              </motion.div>
              {section.image && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="relative overflow-hidden rounded-2xl aspect-[4/3] border border-slate-200"
                >
                  <picture>
                    <source srcSet={section.image.replace(/\.(png|jpe?g)$/i, ".webp")} type="image/webp" />
                    <img src={section.image} alt={section.image_alt || ""} width={800} height={600} loading="lazy" decoding="async" className="h-full w-full object-cover" style={{ backgroundColor: "#F1F5F9" }} />
                  </picture>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      );

    case "definition_list":
      return (
        <section className="bg-[hsl(var(--aoa-gray-bg))]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.h2 {...fadeUp} className="aoa-h2 text-[hsl(var(--aoa-navy))] mb-10 max-w-3xl">
              {section.heading}
            </motion.h2>
            <dl className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white overflow-hidden">
              {section.items.map((row, i) => (
                <motion.div
                  key={row.label}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-2 md:gap-6 px-6 py-5"
                >
                  <dt className="text-sm font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))]">
                    {row.label}
                  </dt>
                  <dd className="text-base text-slate-700 leading-relaxed">{row.value}</dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </section>
      );

    case "numbered_list":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.div {...fadeUp} className="mb-12 max-w-2xl">
              <span className="aoa-eyebrow">Priorities</span>
              <h1 className="aoa-h1 mt-3 text-[hsl(var(--aoa-navy))]">{section.heading}</h1>
            </motion.div>
            <ol className="grid gap-5 md:grid-cols-2">
              {section.items.map((it, i) => (
                <motion.li
                  key={it.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className="aoa-card flex gap-5"
                >
                  <div className="flex-none font-display text-2xl font-bold text-[hsl(var(--aoa-royal))] tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">
                      {it.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{it.body}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>
      );

    case "card_grid":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.div {...fadeUp} className="mb-12 max-w-2xl">
              <span className="aoa-eyebrow">{pageTitle}</span>
              <h1 className="aoa-h1 mt-3 text-[hsl(var(--aoa-navy))]">{section.heading}</h1>
            </motion.div>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {section.items.map((c, i) => (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className="aoa-card"
                >
                  <div className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[hsl(var(--aoa-royal))]">
                    <Icons.Layers className="h-3.5 w-3.5" />
                    Organ
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[hsl(var(--aoa-navy))]">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "tier_grid":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.div {...fadeUp} className="mb-12 max-w-2xl">
              <span className="aoa-eyebrow">Categories</span>
              <h1 className="aoa-h1 mt-3 text-[hsl(var(--aoa-navy))]">{section.heading}</h1>
            </motion.div>
            <div className="grid gap-6 md:grid-cols-3">
              {section.items.map((t, i) => (
                <motion.div
                  key={t.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className={[
                    "relative rounded-2xl border p-8 transition-all duration-300",
                    i === 0
                      ? "bg-[hsl(var(--aoa-navy))] border-[hsl(var(--aoa-navy))] text-white"
                      : "bg-white border-slate-200 text-[hsl(var(--aoa-navy))] hover:border-[hsl(var(--aoa-royal))]",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em]",
                      i === 0 ? "text-[hsl(var(--aoa-sky-light))]" : "text-[hsl(var(--aoa-royal))]",
                    ].join(" ")}
                  >
                    <Icons.Award className="h-3.5 w-3.5" />
                    Tier {i + 1}
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-bold">{t.title}</h3>
                  <p
                    className={[
                      "mt-4 text-sm leading-relaxed",
                      i === 0 ? "text-slate-200" : "text-slate-600",
                    ].join(" ")}
                  >
                    {t.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "chip_grid":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.div {...fadeUp} className="mb-10 max-w-2xl">
              <span className="aoa-eyebrow">Stakeholders</span>
              <h1 className="aoa-h1 mt-3 text-[hsl(var(--aoa-navy))]">{section.heading}</h1>
            </motion.div>
            <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {section.items.map((label, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.025 }}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-[hsl(var(--aoa-gray-bg))] px-4 py-3 text-sm text-[hsl(var(--aoa-navy))] hover:border-[hsl(var(--aoa-royal))] transition-colors"
                >
                  <Icons.Users className="h-4 w-4 text-[hsl(var(--aoa-royal))]" />
                  {label}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "hero_compact":
      return (
        <section className="bg-[hsl(var(--aoa-navy))] text-white" data-testid={`section-${section.key}`}>
          <div className="aoa-grain absolute inset-0 opacity-40 pointer-events-none" />
          <div className="aoa-container py-20 md:py-24">
            <motion.div {...fadeUp} className="max-w-3xl">
              <span className="aoa-eyebrow text-[hsl(var(--aoa-sky-light))]">{pageTitle}</span>
              <h1 className="aoa-h1 mt-4">{section.heading}</h1>
              {section.body && (
                <p className="mt-5 text-lg text-slate-200/90 leading-relaxed max-w-2xl">{section.body}</p>
              )}
            </motion.div>
          </div>
        </section>
      );

    case "download_list":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-16">
            <motion.h2 {...fadeUp} className="aoa-h2 text-[hsl(var(--aoa-navy))] mb-8">
              {section.heading}
            </motion.h2>
            <div className="grid gap-4 md:grid-cols-2">
              {section.items.map((d) => (
                <a
                  key={d.title}
                  href={d.url === "/api/files/profile" ? profilePdfUrl() : d.url}
                  download={d.filename}
                  data-testid={`download-${d.filename}`}
                  className="group flex items-start gap-5 rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:border-[hsl(var(--aoa-royal))] hover:shadow-[0_12px_40px_-20px_rgba(10,31,68,0.3)]"
                >
                  <div className="grid h-12 w-12 flex-none place-items-center rounded-xl bg-[hsl(var(--aoa-royal))]/8 group-hover:bg-[hsl(var(--aoa-royal))] transition-colors">
                    <Icons.FileText className="h-5 w-5 text-[hsl(var(--aoa-royal))] group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">
                      {d.title}
                    </h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d.description}</p>
                    <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[hsl(var(--aoa-royal))]">
                      Download PDF <Icons.Download className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      );

    case "placeholder_card":
      return (
        <section className="bg-[hsl(var(--aoa-gray-bg))]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-16">
            <motion.div
              {...fadeUp}
              className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center"
            >
              <Icons.BookOpen className="mx-auto h-8 w-8 text-[hsl(var(--aoa-royal))]" />
              <h3 className="mt-4 font-display text-xl font-semibold text-[hsl(var(--aoa-navy))]">
                {section.heading}
              </h3>
              <p className="mt-2 text-sm text-slate-600 max-w-xl mx-auto">{section.body}</p>
            </motion.div>
          </div>
        </section>
      );

    case "empty_state":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-24 md:py-32">
            <motion.div {...fadeUp} className="mx-auto max-w-xl text-center">
              <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[hsl(var(--aoa-gray-bg))]">
                <Icons.PlaneTakeoff className="h-9 w-9 text-[hsl(var(--aoa-royal))]" />
              </div>
              <h1 className="mt-6 font-display text-2xl md:text-3xl font-semibold text-[hsl(var(--aoa-navy))]">
                {section.heading}
              </h1>
              <p className="mt-3 text-base text-slate-600">{section.body}</p>
            </motion.div>
          </div>
        </section>
      );

    case "intro_band":
      return (
        <section className="bg-gradient-to-b from-white to-[#F1F5F9]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20 md:py-28">
            <div className="grid gap-12 lg:grid-cols-[1fr_1fr] items-center">
              <motion.div {...fadeUp}>
                <span className="aoa-eyebrow">Who We Are</span>
                <HeadingTag className={`mt-3 text-[hsl(var(--aoa-navy))] ${isFirst ? "aoa-h1" : "aoa-h2"}`}>{section.heading}</HeadingTag>
                <p className="mt-6 text-lg text-slate-700 leading-relaxed">{section.body}</p>
              </motion.div>
              {section.image && (
                <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative overflow-hidden rounded-2xl aspect-[4/3] border border-slate-200 shadow-[0_30px_60px_-30px_rgba(10,31,68,0.3)]">
                  <picture>
                    <source srcSet={section.image.replace(/\.(png|jpe?g)$/i, ".webp")} type="image/webp" />
                    <img src={section.image} alt="" width={800} height={600} loading="lazy" decoding="async" className="h-full w-full object-cover" style={{ backgroundColor: "#F1F5F9" }} />
                  </picture>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      );

    case "feature_grid_v2":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20 md:py-24">
            <motion.div {...fadeUp} className="mb-12 max-w-2xl">
              <span className="aoa-eyebrow">Our Work</span>
              <HeadingTag className={`mt-3 text-[hsl(var(--aoa-navy))] ${isFirst ? "aoa-h1" : "aoa-h2"}`}>{section.heading}</HeadingTag>
              {section.subheading && <p className="mt-3 text-base text-slate-600">{section.subheading}</p>}
            </motion.div>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {section.items.map((it, i) => (
                <motion.a
                  key={it.title}
                  href={`/what-we-do#${it.anchor}`}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  whileHover={{ y: -3 }}
                  className="group aoa-card block"
                  data-testid={`wwd-card-${it.anchor}`}
                >
                  <div className="mb-5 grid h-12 w-12 place-items-center rounded-xl bg-[hsl(var(--aoa-royal))]/8 transition-transform duration-700 group-hover:rotate-[360deg]">
                    <Icon name={it.icon} className="h-5 w-5 text-[hsl(var(--aoa-royal))]" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">{it.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{it.body}</p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))]">Read more <Icons.ArrowRight className="h-3 w-3" /></div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      );

    case "imagery_band":
      return (
        <section className="bg-[hsl(var(--aoa-navy))] text-white relative overflow-hidden" data-testid={`section-${section.key}`}>
          <div className="aoa-grain absolute inset-0 opacity-40 pointer-events-none" />
          <div className="aoa-container py-20 relative">
            <motion.h2 {...fadeUp} className="aoa-h2 max-w-3xl">{section.heading}</motion.h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {section.items.map((it, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }} className="relative overflow-hidden rounded-2xl border border-white/10 aspect-[4/3] group">
                  <picture>
                    <source srcSet={it.image && it.image.replace(/\.(png|jpe?g)$/i, ".webp")} type="image/webp" />
                    <img src={it.image} alt={it.alt || ""} width={800} height={600} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ backgroundColor: "#F1F5F9" }} />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--aoa-navy))]/90 via-transparent to-transparent" />
                  {it.caption && <div className="absolute bottom-4 left-4 right-4 font-display text-sm font-semibold">{it.caption}</div>}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "callout_small":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-16">
            <motion.div {...fadeUp} className="max-w-3xl rounded-xl border border-[hsl(var(--aoa-navy))]/15 bg-[hsl(var(--aoa-gray-bg))]/50 p-6 shadow-[0_8px_30px_-15px_rgba(10,31,68,0.18)]">
              <h3 className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-[hsl(var(--aoa-royal))]">{section.heading}</h3>
              <p className="mt-2 text-base text-slate-700 leading-relaxed">{section.body}</p>
            </motion.div>
          </div>
        </section>
      );

    case "partners_strip":
      return <PartnersStrip section={section} />;

    case "cta_strip_image":
      return (
        <section className="relative overflow-hidden" data-testid={`section-${section.key}`}>
          <div className="absolute inset-0">
            <picture>
              <source srcSet={section.image && section.image.replace(/\.(png|jpe?g)$/i, ".webp")} type="image/webp" />
              <img src={section.image} alt="" width={1920} height={800} loading="lazy" decoding="async" className="h-full w-full object-cover" style={{ backgroundColor: "#F1F5F9" }} />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--aoa-navy))]/95 via-[hsl(var(--aoa-navy))]/80 to-[hsl(var(--aoa-navy))]/60" />
          </div>
          <div className="aoa-container py-20 relative">
            <motion.div {...fadeUp} className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white max-w-2xl">{section.heading}</h2>
              <div className="flex flex-wrap gap-3">
                {(section.ctas || []).map((c) => <CTA key={c.label} cta={c} isHero />)}
              </div>
            </motion.div>
          </div>
        </section>
      );

    case "deep_dive_row": {
      const imageLeft = section.image_side === "left";
      return (
        <section id={section.anchor} className={`scroll-mt-24 ${section.anchor && (["safety","domestic","passenger-service","training","research"].includes(section.anchor)) ? "bg-[#F8FAFC]" : "bg-white"}`} data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <div className={`grid gap-12 lg:grid-cols-2 items-center ${imageLeft ? "" : ""}`}>
              <motion.div {...fadeUp} className={imageLeft ? "lg:order-2" : ""}>
                <span className="aoa-eyebrow">Focus area</span>
                <h2 className="aoa-h2 mt-3 text-[hsl(var(--aoa-navy))]">{section.heading}</h2>
                <p className="mt-5 text-base text-slate-700 leading-relaxed">{section.body}</p>
                {section.chips && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {section.chips.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--aoa-royal))]/8 px-3 py-1.5 text-xs font-semibold text-[hsl(var(--aoa-royal))]">
                        <Icons.CheckCircle2 className="h-3 w-3" />{c}
                      </span>
                    ))}
                  </div>
                )}
                {section.closing && <p className="mt-5 text-sm text-slate-600 leading-relaxed italic">{section.closing}</p>}
              </motion.div>
              {section.image && (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className={`relative overflow-hidden rounded-2xl aspect-[4/3] border border-slate-200 shadow-[0_30px_60px_-30px_rgba(10,31,68,0.3)] ${imageLeft ? "lg:order-1" : ""}`}>
                  <picture>
                    <source srcSet={section.image.replace(/\.(png|jpe?g)$/i, ".webp")} type="image/webp" />
                    <img src={section.image} alt="" width={800} height={600} loading="lazy" decoding="async" className="h-full w-full object-cover" style={{ backgroundColor: "#F1F5F9" }} />
                  </picture>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      );
    }

    case "bullet_list":
      return (
        <section className="bg-[hsl(var(--aoa-gray-bg))]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.h2 {...fadeUp} className="aoa-h2 text-[hsl(var(--aoa-navy))] mb-8">{section.heading}</motion.h2>
            <ul className="grid gap-3 md:grid-cols-2">
              {section.items.map((b, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }} className="flex gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-700">
                  <Icons.CheckCircle2 className="h-4 w-4 mt-0.5 flex-none text-[hsl(var(--aoa-royal))]" />{b}
                </motion.li>
              ))}
            </ul>
          </div>
        </section>
      );

    case "membership_value":
      return (
        <section className="bg-[#F8FAFC]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-20">
            <motion.div {...fadeUp} className="mb-10 max-w-2xl">
              <span className="aoa-eyebrow">Value</span>
              <h2 className="aoa-h2 mt-3 text-[hsl(var(--aoa-navy))]">{section.heading}</h2>
              {section.subheading && <p className="mt-3 text-base text-slate-600">{section.subheading}</p>}
            </motion.div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {section.items.map((it, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.03 }} className="aoa-card flex gap-3">
                  <div className="grid h-8 w-8 flex-none place-items-center rounded-full bg-[hsl(var(--aoa-royal))]/10 text-xs font-bold text-[hsl(var(--aoa-royal))]">{String(i + 1).padStart(2, "0")}</div>
                  <p className="text-sm text-slate-700 leading-relaxed">{it}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "chip_grid_compact":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-16">
            <motion.h2 {...fadeUp} className="aoa-h2 text-[hsl(var(--aoa-navy))] mb-6 max-w-3xl">{section.heading}</motion.h2>
            <div className="flex flex-wrap gap-2">
              {section.items.map((s, i) => (
                <motion.span key={s} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: i * 0.02 }} className="aoa-chip cursor-default">
                  <Icons.Dot className="h-4 w-4 text-[hsl(var(--aoa-royal))]" />{s}
                </motion.span>
              ))}
            </div>
          </div>
        </section>
      );

    case "resource_categories":
      return (
        <section className="bg-[#F8FAFC]" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-16">
            <motion.div {...fadeUp} className="mb-8 max-w-2xl">
              <h2 className="aoa-h2 text-[hsl(var(--aoa-navy))]">{section.heading}</h2>
              {section.subheading && <p className="mt-2 text-base text-slate-600">{section.subheading}</p>}
            </motion.div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {section.items.map((it, i) => (
                <motion.div key={it.title} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }} className="aoa-card relative">
                  {it.coming_soon && <span className="absolute top-3 right-3 rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Coming soon</span>}
                  <Icons.FileText className="h-5 w-5 text-[hsl(var(--aoa-royal))]" />
                  <h3 className="mt-3 font-display text-base font-semibold text-[hsl(var(--aoa-navy))]">{it.title}</h3>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">{it.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );

    case "legal_content":
      return (
        <section className="bg-white" data-testid={`section-${section.key}`}>
          <div className="aoa-container py-16 max-w-3xl mx-auto">
            <div className="prose prose-slate max-w-none text-[15px] leading-relaxed prose-headings:font-display prose-headings:text-[hsl(var(--aoa-navy))]" dangerouslySetInnerHTML={{ __html: section.body_html || "" }} />
          </div>
        </section>
      );

    default:
      return null;
  }
}
